import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaUserCircle,
  FaUsers,
  FaBuilding,
  FaHandshake,
  FaProjectDiagram,
  FaUserFriends,
  FaFileContract,
  FaPalette,
  FaCreditCard,
  FaCog,
  FaTasks,
  FaSignOutAlt,
  FaBars,
  FaTimes,
} from "react-icons/fa";
import Swal from "sweetalert2";
import "./Sidebar.css";
import A2ZLogo from "../../Shared/Images/A2Zlogo.jpeg";
import { API_BASE } from "../../../ApiUrl";

const MENU_ITEMS = [
  {
    label: "Profile",
    icon: FaUserCircle,
    to: "/admin/profile",
    paths: ["/admin/profile"],
  },
  {
    label: "Surveyors",
    icon: FaUsers,
    to: "/admin/users-registrations",
    paths: [
      "/admin/users-registrations",
      "/admin/calculation-rules",
      "/admin/calculation-rules/add",
    ],
  },
  {
    label: "Organizations",
    icon: FaBuilding,
    to: "/admin/myprofile",
    paths: [
      "/admin/myprofile",
      "/admin/operations",
      "/admin/organization/add",
      "/admin/legalentities",
      "/admin/locations",
      "/admin/contacts",
    ],
  },
  {
    label: "Clients",
    icon: FaHandshake,
    to: "/admin/clients",
    paths: ["/admin/clients", "/admin/other-organizations"],
  },
  {
    label: "Projects",
    icon: FaProjectDiagram,
    to: "/admin/projects",
    paths: [
      "/admin/projects",
      "/admin/project-membership",
      "/admin/project-requirement",
      "/admin/project-candidates",
      "/admin/deployment",
    ],
  },
  {
    label: "People",
    icon: FaUserFriends,
    to: "/admin/user",
    paths: ["/admin/user", "/admin/tenantinvitation"],
  },
  {
    label: "Document & Compliance",
    icon: FaFileContract,
    to: "/admin/document",
    paths: ["/admin/document", "/admin/legalsettings/add", "/admin/nda/add"],
  },
  {
    label: "Branding & Templates",
    icon: FaPalette,
    to: "/admin/branding",
    paths: ["/admin/branding", "/admin/report-template"],
  },
  {
    label: "Subscription",
    icon: FaCreditCard,
    to: "/admin/subscriptions",
    paths: ["/admin/subscriptions", "/admin/add/subscription", "/admin/module-entitlement"],
  },
  {
    label: "Settings",
    icon: FaCog,
    to: "/admin/settings/add",
    paths: [
      "/admin/settings/add",
      "/admin/domain/add",
      "/admin/security-settings",
      "/admin/billing",
      "/admin/integration",
      "/admin/terminology",
      "/admin/numbering",
      "/admin/notification-setting",
      "/admin/approvalmatrix",
    ],
  },
  {
    label: "Activity & Workflows",
    icon: FaTasks,
    to: "/admin/work-flow",
    paths: ["/admin/work-flow", "/admin/operationlog"],
  },
];

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Read cached verification status synchronously (instant first render, no spinner)
  const [verificationStatus, setVerificationStatus] = useState(
    () => localStorage.getItem("tenant_verification_status") || ""
  );

  const isActive = (paths) => {
    const pathArray = Array.isArray(paths) ? paths : [paths];
    return pathArray.some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  // Lets the Header follow the sidebar width (see CSS)
  useEffect(() => {
    document.body.classList.toggle("ta-sidebar-collapsed", isCollapsed);
    return () => document.body.classList.remove("ta-sidebar-collapsed");
  }, [isCollapsed]);

  // Close mobile drawer on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // ---------------------------------------------------------
  // Fetch latest verification status from /tenant-verifications/
  // ---------------------------------------------------------
  useEffect(() => {
    const adminData = localStorage.getItem("tenant_user");
    if (!adminData) return;

    let tenantId = null;
    try {
      const parsed = JSON.parse(adminData);
      tenantId = parsed?.tenant_id;
    } catch (e) {
      console.error("Error parsing tenant_user:", e);
    }

    if (!tenantId) return;

    let cancelled = false;

    const fetchVerificationStatus = async () => {
      try {
        const res = await fetch(`${API_BASE}/tenant-verifications/`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        if (!res.ok) throw new Error("Failed to fetch verifications");

        const data = await res.json();
        const list = data?.data;

        if (!Array.isArray(list) || cancelled) return;

        const tenantVerifications = list.filter(
          (v) => Number(v.tenant) === Number(tenantId)
        );

        let status = "";
        if (tenantVerifications.length > 0) {
          const latest = [...tenantVerifications].sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          )[0];
          status = latest?.status || "";
        }

        status = String(status).toUpperCase();

        if (cancelled) return;

        setVerificationStatus(status);

        if (status) {
          localStorage.setItem("tenant_verification_status", status);
        } else {
          localStorage.removeItem("tenant_verification_status");
        }
      } catch (error) {
        console.error("Error fetching verification status:", error);
        // Keep whatever cached value we already have
      }
    };

    fetchVerificationStatus();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]); // refetch on route change (e.g. after admin approves)

  // Keep in sync if Profile (or another tab) updates the cached status
  useEffect(() => {
    const sync = () =>
      setVerificationStatus(
        localStorage.getItem("tenant_verification_status") || ""
      );

    window.addEventListener("verification-status-updated", sync);
    window.addEventListener("storage", sync);

    return () => {
      window.removeEventListener("verification-status-updated", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const toggleSidebar = () => setIsCollapsed((prev) => !prev);
  const toggleMobileSidebar = () => setIsMobileOpen((prev) => !prev);

  const fetchUserDetails = async (userId) => {
    try {
      const response = await fetch(`${API_BASE}/users/${userId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to fetch user details");

      const data = await response.json();
      setUserDetails(data?.data || null);
    } catch (error) {
      console.error("Error fetching user details:", error);

      const adminData = localStorage.getItem("tenant_user");
      if (adminData) {
        try {
          const parsedUser = JSON.parse(adminData);
          setUserDetails({
            full_name: parsedUser.full_name || "",
            email: parsedUser.email || "",
            role: parsedUser.role,
          });
        } catch (e) {
          console.error("Error parsing user data:", e);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const adminData = localStorage.getItem("tenant_user");

    if (adminData) {
      try {
        const parsedUser = JSON.parse(adminData);
        setUser(parsedUser);

        if (parsedUser.user_id) {
          fetchUserDetails(parsedUser.user_id);
        } else {
          setUserDetails({
            first_name: parsedUser.full_name || "",
            email: parsedUser.email || "",
            role: parsedUser.role || "Admin",
          });
          setLoading(false);
        }
      } catch (error) {
        console.error("Error parsing admin user data:", error);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will be logged out of the system",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3b82f6",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, logout",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem("tenant_user");
        localStorage.removeItem("admin_token");
        localStorage.removeItem("tenant_verification_status");

        Swal.fire({
          icon: "success",
          title: "Logged Out!",
          text: "You have been successfully logged out.",
          timer: 1500,
          showConfirmButton: false,
        });

        navigate("/Oceanstar/Login");
      }
    });
  };

  const displayName =
    userDetails?.full_name ||
    userDetails?.first_name ||
    user?.full_name ||
    "Admin";

  const displayEmail = userDetails?.email || user?.email || "";

  const getInitials = () => {
    if (displayName && displayName !== "Admin") {
      const nameParts = displayName.split(" ");
      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
      }
      return displayName.substring(0, 2).toUpperCase();
    }
    return "TA";
  };

  // ---------------------------------------------------------
  // Menu visibility rules:
  //   APPROVED      -> hide Profile, show everything else
  //   anything else -> show only Profile
  // ---------------------------------------------------------
  const isApproved =
    String(verificationStatus).toUpperCase() === "APPROVED";

  const visibleMenuItems = isApproved
    ? MENU_ITEMS.filter((item) => item.label !== "Profile")
    : MENU_ITEMS.filter((item) => item.label === "Profile");

  return (
    <>
      {/* Mobile Overlay */}
      <div
        className={`ta-mobile-overlay ${isMobileOpen ? "show" : ""}`}
        onClick={toggleMobileSidebar}
      />

      <aside
        className={`ta-sidebar ${isCollapsed ? "collapsed" : ""} ${
          isMobileOpen ? "mobile-open" : ""
        }`}
      >
        {/* Header: logo + name + menu bar button */}
        <div className="ta-sidebar-header">
          <div className="ta-logo-icon">
            <img src={A2ZLogo} alt="A2Z Logo" />
          </div>

          <div className="ta-sidebar-header-text">
            <h6>Oceanstar Admin</h6>
            <small>Surveyor Management</small>
          </div>

          <button
            className="ta-sidebar-collapse-btn"
            onClick={toggleSidebar}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label="Toggle sidebar"
          >
            <FaBars />
          </button>
        </div>

        {/* Scrollable Menu */}
        <nav className="ta-sidebar-scroll">
          {visibleMenuItems.map(({ label, icon: Icon, to, paths }) => (
            <div className="ta-menu-section" key={to}>
              <Link
                to={to}
                title={label}
                className={`ta-menu-title ${
                  isActive(paths) ? "active" : ""
                }`}
              >
                <span className="ta-menu-icon">
                  <Icon />
                </span>
                <span className="ta-menu-label">{label}</span>
              </Link>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="ta-sidebar-footer">
          <div className="ta-user-info-wrapper">
            <div className="ta-avatar" title={displayName}>
              {loading ? "..." : getInitials()}
            </div>

            <div className="ta-user-details">
              <p className="ta-sidebar-user-name">
                {loading ? "Loading..." : displayName}
              </p>
              <span className="ta-user-email">
                {loading ? "Loading..." : displayEmail}
              </span>
            </div>

            <button
              className="ta-logout-btn"
              onClick={handleLogout}
              title="Logout"
            >
              <FaSignOutAlt />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Toggle Button */}
      <button className="ta-mobile-toggle" onClick={toggleMobileSidebar}>
        {isMobileOpen ? <FaTimes /> : <FaBars />}
      </button>
    </>
  );
};

export default Sidebar;